/**
 * ProjectTypeSelector - Visual grid for selecting project type
 * Stitch design system
 */
import React from 'react';
import { Box, Typography } from '@mui/material';

const T = {
  primary: '#526075', primaryContainer: '#d5e3fd',
  tertiary: '#635983', tertiaryContainer: '#dbcdff',
  surface: '#ffffff', surfaceLow: '#f1f4f5',
  text: '#2d3335', secondary: '#556071', outline: '#767c7e', outlineVariant: '#adb3b5',
  font: '"Manrope", system-ui, sans-serif',
  fontBody: '"Inter", system-ui, sans-serif',
  shadow: '0 1px 3px rgba(45,51,53,0.04)',
};

const DEFAULT_PROJECT_TYPES = [
  { code: 'commercial', name: 'Commercial', icon: '📺', description: 'Reclame spots en advertenties', default_conditions: { location_needed: true, cast_needed: true, props_needed: true } },
  { code: 'corporate', name: 'Corporate Video', icon: '🏢', description: 'Bedrijfsvideo\'s, testimonials', default_conditions: { script_needed: true } },
  { code: 'event', name: 'Event Coverage', icon: '📹', description: 'Evenement vastlegging', default_conditions: { multiple_locations: true } },
  { code: 'interview', name: 'Interview', icon: '🎤', description: 'Interview video\'s', default_conditions: { script_needed: true } },
  { code: 'music_video', name: 'Music Video', icon: '🎵', description: 'Muziekvideo\'s', default_conditions: { location_needed: true, cast_needed: true, props_needed: true } },
  { code: 'documentary', name: 'Documentary', icon: '🎬', description: 'Documentaires en reportages', default_conditions: { multiple_locations: true, script_needed: true } },
  { code: 'animation', name: 'Animation', icon: '✨', description: 'Animatie en motion graphics', default_conditions: { animation_needed: true, script_needed: true } },
  { code: 'social', name: 'Social Media', icon: '📱', description: 'Social media content', default_conditions: {} },
];

function ProjectTypeSelector({
  projectTypes = DEFAULT_PROJECT_TYPES,
  selectedType,
  onSelectType,
  onConditionsChange,
}) {
  const handleSelect = (type) => {
    onSelectType(type.code);
    if (onConditionsChange && type.default_conditions) {
      onConditionsChange(type.default_conditions);
    }
  };

  const types = projectTypes.length > 0 ? projectTypes : DEFAULT_PROJECT_TYPES;

  return (
    <Box>
      <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.68rem', fontWeight: 700, color: T.outline, textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1.5 }}>
        Selecteer project type
      </Typography>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)' }, gap: 1.5 }}>
        {types.map((type) => {
          const isSelected = selectedType === type.code;
          return (
            <Box
              key={type.code}
              onClick={() => handleSelect(type)}
              sx={{
                p: 2, cursor: 'pointer', textAlign: 'center',
                borderRadius: '0.75rem',
                bgcolor: isSelected ? T.primaryContainer : T.surface,
                border: `2px solid ${isSelected ? `${T.primary}40` : 'transparent'}`,
                boxShadow: T.shadow,
                transition: 'all 0.15s',
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                minHeight: 110,
                '&:hover': {
                  borderColor: isSelected ? `${T.primary}40` : `${T.primary}20`,
                  boxShadow: '0 8px 24px rgba(45,51,53,0.08)',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              <Typography sx={{ fontSize: '1.75rem', mb: 0.75 }}>
                {type.icon}
              </Typography>
              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.82rem', fontWeight: 700, color: T.text, mb: 0.25 }}>
                {type.name}
              </Typography>
              <Typography sx={{
                fontFamily: T.fontBody, fontSize: '0.68rem', color: T.outline, lineHeight: 1.3,
                display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
              }}>
                {type.description}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

export default ProjectTypeSelector;
